import {
  ENDPOINTS,
  SearchRequestSchema,
  SearchSelectionRequestSchema,
  type SearchRequest,
  type SearchResponse,
  type SearchSelectionRequest,
  type SearchSelectionResponse,
} from '@clm/shared-types';

import { apiPost } from './client';

/**
 * `search` and `record-search-selection`.
 *
 * Both are POSTs, so there is no query flattening to do — but two contract
 * facts drive how the surface above uses them:
 *
 *  1. `SearchResponseSchema` is discriminated on `target`, so a caller cannot
 *     read clause hits out of a contract search without narrowing the union.
 *     The wrapper returns the union and lets the surface narrow, rather than
 *     offering two functions that would let a mismatched target through.
 *  2. `SearchRequestSchema` is `.strict()` and `q` is `.trim().min(1)`, so an
 *     empty query is a ZodError here and never a request. Whitespace is not a
 *     query, and the contract says so rather than a handler.
 *
 * No LLM is anywhere near this path (CLAUDE.md rule 14, NFR-10, FR-27, O-6);
 * the browser posts a string and renders what the index returns, including the
 * `reason_trace` the search service composed. The trace's `detail` strings are
 * the compliance artefact — they are displayed verbatim and never re-worded
 * here, because they have to read the same in the admin UI, in a log and in an
 * evidence pack.
 */

const SEARCH_ENDPOINT = ENDPOINTS.search;
const SELECTION_ENDPOINT = ENDPOINTS['record-search-selection'];

export async function runSearch(
  request: SearchRequest,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const body = SearchRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    SEARCH_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return SEARCH_ENDPOINT.response.parse(raw);
}

/**
 * Which result the user actually opened — WP-09 §5 stage ⑥.
 *
 * Fire-and-forget from the surface's point of view: this is search telemetry,
 * and a failure to record it must never stop a user opening a contract. Keyed
 * on `search_id` from the response so it joins to the logged query.
 */
export async function recordSearchSelection(
  request: SearchSelectionRequest,
  signal?: AbortSignal,
): Promise<SearchSelectionResponse> {
  const body = SearchSelectionRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    SELECTION_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return SELECTION_ENDPOINT.response.parse(raw);
}
